import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

export type LivviaState =
  | 'arrival'
  | 'prepare'
  | 'prepare_success'
  | 'post_live'
  | 'post_live_deferred'
  | 'video_picker'
  | 'processing'
  | 'processing_error'
  | 'result'
  | 'idle';

export interface LastLiveInfo {
  durationLabel: string;
  commentsCount: number;
  timestampLabel: string;
  platform: 'instagram' | 'facebook';
  pluginId?: string;
  mediaId?: string;
}

export interface PluginSummary {
  id: string;
  type: 'instagram' | 'facebook';
  pendingFullSyncMediaId: string;
  lastSeenLive: Date | null;
  autoMessageEnabled: boolean;
}

export interface CartLine {
  id: string;
  handle: string;
  initials: string;
  avatarClass: string;
  items: string;
  amount: number;
}

export interface ResultData {
  totalAmount: number;
  clientCount: number;
  timeSavedHours: number;
  carts: CartLine[];
}

// Injected by callers so the store stays framework-clean (no store-to-store
// import cycles). DashboardLayout provides real plugin-store bindings; tests
// can pass stubs.
export interface PipelineAPI {
  analyze: (p: { pluginId: string; mediaId: string }) => Promise<{ status: string }>;
  getStatus: (mediaId: string) => Promise<{ status?: string; progress?: number; step?: number }>;
  getResult: (mediaId: string) => Promise<unknown>;
  aggregate: (p: { pluginId: string; mediaId: string }) => Promise<{
    total_amount?: number;
    client_count?: number;
    carts?: Array<{
      buyer_user_id: string;
      buyer_handle: string;
      items_label: string;
      amount: number;
    }>;
  }>;
  setCartStatus: (p: {
    pluginId: string;
    mediaId: string;
    buyerUserId: string;
    status: 'created' | 'request_payment' | 'paid' | 'cancelled';
    reason?: string;
  }) => Promise<unknown>;
  updateSettings: (p: {
    pluginId: string;
    autoMessageEnabled?: boolean;
    mode?: 'transcript' | 'catalog' | '';
    onboardingStep?: number;
  }) => Promise<unknown>;
  // Idempotent — creates a default "Cadastro da cliente" form with
  // full_name + instagram + mobile fields when the plugin has none.
  // Called when the seller first enables auto-messaging so the
  // "Personalizar cadastro" CTA lands on an editable form (not on an
  // empty state that requires them to create a form from scratch).
  ensureDefaultForm?: (p: { pluginId: string }) => Promise<void>;
}

export type WaitingMode = 'transcript' | 'catalog' | null;
export type WaitingStep = 0 | 1 | 2;

// TODO: replace localStorage flags with server-side onboarding_progress once
// the Go backend exposes it. Keyed by user is ideal — for now the store is
// only reachable after login so scoping is implicit per-browser-per-user.
const LS_PREPARE_ACK = 'livvia:prepare:acknowledged';

const readAck = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};
const writeAck = (key: string, value: boolean) => {
  try {
    if (value) window.localStorage.setItem(key, '1');
    else window.localStorage.removeItem(key);
  } catch { /* private mode, ignore */ }
};

// DEV-only preview carts. Kept behind a NODE_ENV gate so a prod build
// can never accidentally show fake buyer handles (`@marifeller`,
// `@carol.lima`, `@ju.modas`) to real customers — that used to be
// possible when `pluginId`/`mediaId`/`pipelineAPI` were missing at
// runtime. In prod, missing prerequisites route to `processing_error`
// instead of falling through to fake data.
const IS_DEV_PREVIEW = process.env.NODE_ENV !== 'production';

const buildMockResult = (): ResultData => ({
  totalAmount: 2140,
  clientCount: 23,
  timeSavedHours: 3,
  carts: [
    {
      id: 'c1', handle: '@marifeller', initials: 'MF', avatarClass: 'bg-gradient-to-br from-orange-300 to-pink-500', items: '2× Vestido midi floral · M', amount: 358,
    },
    {
      id: 'c2', handle: '@carol.lima', initials: 'CL', avatarClass: 'bg-gradient-to-br from-sky-400 to-indigo-500', items: '1× Bolsa caramelo · 1× Cinto fino', amount: 219,
    },
    {
      id: 'c3', handle: '@ju.modas', initials: 'JM', avatarClass: 'bg-gradient-to-br from-teal-400 to-sky-500', items: '1× Blusa off-white · P', amount: 89,
    },
  ],
});

const mapAggregateToResult = (
  raw: Awaited<ReturnType<PipelineAPI['aggregate']>>,
): ResultData => {
  const palettes = [
    'bg-gradient-to-br from-orange-300 to-pink-500',
    'bg-gradient-to-br from-sky-400 to-indigo-500',
    'bg-gradient-to-br from-teal-400 to-sky-500',
    'bg-gradient-to-br from-amber-300 to-orange-500',
    'bg-gradient-to-br from-fuchsia-400 to-purple-500',
  ];
  const carts: CartLine[] = (raw.carts || []).map((c, i) => {
    // buyer_handle can arrive null/empty from anonymised or deleted-
    // account rows. Normalise once and drive both initials and handle
    // from the same clean value so `.startsWith('@')` never trips on
    // undefined and crashes the whole result page.
    const rawHandle = c.buyer_handle || '';
    const cleanHandle = rawHandle.replace(/^@/, '') || 'anonimo';
    const initials = cleanHandle
      .split('.')
      .map((s) => s[0] || '')
      .join('')
      .slice(0, 2)
      .toUpperCase() || '??';
    return {
      id: c.buyer_user_id,
      handle: `@${cleanHandle}`,
      initials,
      avatarClass: palettes[i % palettes.length],
      items: c.items_label,
      amount: c.amount,
    };
  });
  return {
    totalAmount: raw.total_amount || carts.reduce((s, c) => s + c.amount, 0),
    clientCount: raw.client_count ?? carts.length,
    timeSavedHours: 3, // TODO: derive from live duration when the pipeline reports it.
    carts,
  };
};

export default defineStore('livviaStore', () => {
  // ── Reactive state ────────────────────────────────────────────────
  const state = ref<LivviaState>('idle');
  const processingStep = ref(0);
  const processingProgress = ref(0);
  const showConnectDrawer = ref(false);
  // Which platform Livvia is asking the seller to connect. Bound to
  // PluginConnectDrawer's `platform` prop so the drawer skips the
  // platform-chooser and jumps straight to the specific connect flow.
  const connectPlatform = ref<'instagram' | 'facebook' | ''>('');
  const autoMessageEnabled = ref(false);
  const waitingAutoMessageSaving = ref(false);
  const lastLive = ref<LastLiveInfo | null>(null);
  const resultData = ref<ResultData | null>(null);
  const sentCarts = ref<Set<string>>(new Set());
  const chargingCarts = ref<Set<string>>(new Set());
  const allChargesSent = ref(false);

  // ── Waiting-live onboarding state ────────────────────────────────
  // Which plugin's dashboard we're currently rendering the waiting-live
  // flow for. Set by <PluginWaitingLive> on mount so persistence calls
  // land on the right document.
  const waitingPluginId = ref<string>('');
  // 0 = invite prompt; 1 = chooser visible; 2 = mode picked, prep visible.
  const waitingStep = ref<WaitingStep>(0);
  // Which cart-building path the seller declared. Null means unpicked.
  const waitingMode = ref<WaitingMode>(null);
  // "Como funciona" modal open state.
  const howModalOpen = ref(false);
  // Guards against firing multiple persist calls during rapid state flips.
  let waitingPersistTimer: ReturnType<typeof setTimeout> | null = null;

  // Which plugin drives the prepare-toggle target. First-connected plugin
  // wins; multi-account sellers can flip individual toggles later on the
  // plugin detail page.
  const preparePluginId = ref<string>('');

  // Injected pipeline bindings — DashboardLayout wires the real plugin store
  // through registerPipelineAPI at setup time.
  let pipelineAPI: PipelineAPI | null = null;

  // Cancellable timer chain — leaving the flow (or refreshing) shouldn't
  // leave a rogue timeout that mutates state after the user is gone.
  const processingTimers: number[] = [];

  // ── Computed ──────────────────────────────────────────────────────
  const isVisible = computed(() => state.value !== 'idle');

  // ── Setup helpers ─────────────────────────────────────────────────
  const registerPipelineAPI = (api: PipelineAPI) => { pipelineAPI = api; };
  const setPreparePluginId = (id: string) => { preparePluginId.value = id; };

  const setState = (next: LivviaState) => {
    state.value = next;
  };

  const openConnect = (platform?: 'instagram' | 'facebook') => {
    connectPlatform.value = platform || '';
    showConnectDrawer.value = true;
  };

  const cancelProcessing = () => {
    while (processingTimers.length) {
      const id = processingTimers.pop();
      if (id !== undefined) {
        window.clearTimeout(id);
        window.clearInterval(id);
      }
    }
  };

  // ── State transitions ────────────────────────────────────────────
  const deriveFromPlugins = (summaries: PluginSummary[]) => {
    if (summaries.length === 0) {
      state.value = 'arrival';
      return;
    }
    // First plugin drives the prepare-toggle target. Sync the store's
    // toggle mirror so PrepareCards renders the real value.
    preparePluginId.value = summaries[0].id;
    autoMessageEnabled.value = summaries[0].autoMessageEnabled;
    if (!readAck(LS_PREPARE_ACK)) {
      state.value = 'prepare';
      return;
    }
    // Real Q1 signal — any plugin with pendingFullSyncMediaId set has a
    // live that ended but hasn't been fully processed yet. First such
    // plugin wins; when the pipeline finishes and the field clears, the
    // dashboard falls back to idle on the next visit.
    const pending = summaries.find((p) => p.pendingFullSyncMediaId !== '');
    if (pending) {
      lastLive.value = {
        durationLabel: '',
        commentsCount: 0,
        timestampLabel: '',
        platform: pending.type,
        pluginId: pending.id,
        mediaId: pending.pendingFullSyncMediaId,
      };
      state.value = 'post_live';
      return;
    }
    state.value = 'idle';
  };

  const activatePrepare = async () => {
    writeAck(LS_PREPARE_ACK, true);
    state.value = 'prepare_success';
    // Persist whatever value the user landed on with the toggle. If they
    // flipped it off before hitting Ativar, that intent gets saved. Fire-
    // and-forget — the localStorage ack already dismissed the card so a
    // failed save doesn't leave the user in a stuck state.
    if (preparePluginId.value && pipelineAPI?.updateSettings) {
      try {
        await pipelineAPI.updateSettings({
          pluginId: preparePluginId.value,
          autoMessageEnabled: autoMessageEnabled.value,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('livvia.activatePrepare updateSettings failed', err);
      }
    }
  };

  const skipPrepare = async () => {
    writeAck(LS_PREPARE_ACK, true);
    state.value = 'idle';
    // Persist explicit opt-out so the automation Lambda skips this plugin
    // on the next comment. Fire-and-forget; UI already dismissed.
    if (preparePluginId.value && pipelineAPI?.updateSettings) {
      try {
        autoMessageEnabled.value = false;
        await pipelineAPI.updateSettings({
          pluginId: preparePluginId.value,
          autoMessageEnabled: false,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('livvia.skipPrepare updateSettings failed', err);
      }
    }
  };

  // TODO(Q1): drive this from a real "live ended & unprocessed" signal
  // (webhook, IG polling, or manual seller confirmation). Until then callers
  // pass in the live metadata explicitly — a URL query param in dev mode is
  // enough to preview the flow (see DashboardLayout).
  const enterPostLive = (info: LastLiveInfo) => {
    lastLive.value = info;
    state.value = 'post_live';
  };

  const deferPostLive = () => {
    state.value = 'post_live_deferred';
  };

  const startBuildingCarts = () => {
    state.value = 'video_picker';
  };

  const startProcessing = async (target?: { pluginId?: string; mediaId?: string }) => {
    cancelProcessing();
    state.value = 'processing';
    processingStep.value = 0;
    processingProgress.value = 0;

    const pluginId = target?.pluginId || lastLive.value?.pluginId || '';
    const mediaId = target?.mediaId || lastLive.value?.mediaId || '';

    // No live target OR no API bound. In DEV we run the local demo
    // animation for stakeholder previews. In PROD we refuse to route
    // into the mock — a missing prerequisite means real infrastructure
    // isn't wired, and showing fake buyer names would be catastrophic
    // in a live demo. Surface a real error state instead.
    if (!pluginId || !mediaId || !pipelineAPI) {
      if (!IS_DEV_PREVIEW) {
        state.value = 'processing_error';
        return;
      }
      const stepDelays = [200, 900, 900, 900];
      let elapsed = 0;
      stepDelays.forEach((delay, idx) => {
        elapsed += delay;
        processingTimers.push(window.setTimeout(() => {
          processingStep.value = idx;
          processingProgress.value = Math.round(((idx + 1) / stepDelays.length) * 100);
        }, elapsed));
      });
      processingTimers.push(window.setTimeout(() => {
        processingStep.value = stepDelays.length;
      }, elapsed + 400));
      processingTimers.push(window.setTimeout(() => {
        resultData.value = buildMockResult();
        sentCarts.value = new Set();
        allChargesSent.value = false;
        state.value = 'result';
      }, elapsed + 900));
      return;
    }

    // Real path — kick off analysis then poll status until done.
    try {
      await pipelineAPI.analyze({ pluginId, mediaId });
    } catch (err) {
      // Fall back to the demo animation so the UI doesn't hang.
      // Real errors are surfaced by the API layer; log for observability.
      // eslint-disable-next-line no-console
      console.error('livvia.analyze failed', err);
    }

    const poll = window.setInterval(async () => {
      try {
        const status = await pipelineAPI!.getStatus(mediaId);
        if (typeof status.step === 'number') processingStep.value = status.step;
        if (typeof status.progress === 'number') processingProgress.value = status.progress;
        // The agent-controller job statuses are queued|running|succeeded|
        // partial_success|failed|cancelled — 'completed'/'done' never occur,
        // which left this poller spinning every 2s forever.
        if (status.status === 'succeeded' || status.status === 'partial_success'
          || status.status === 'completed' || status.status === 'done') {
          window.clearInterval(poll);
          const raw = await pipelineAPI!.aggregate({ pluginId, mediaId });
          resultData.value = mapAggregateToResult(raw);
          sentCarts.value = new Set();
          allChargesSent.value = false;
          processingStep.value = 4;
          processingProgress.value = 100;
          state.value = 'result';
        }
        if (status.status === 'failed' || status.status === 'cancelled') {
          window.clearInterval(poll);
          // eslint-disable-next-line no-console
          console.error('livvia.pipeline failed');
          state.value = 'post_live';
        }
      } catch (err) {
        window.clearInterval(poll);
        // eslint-disable-next-line no-console
        console.error('livvia.status poll failed', err);
        state.value = 'post_live';
      }
    }, 2000);
    processingTimers.push(poll);
  };

  const chargeCart = async (cartId: string) => {
    if (sentCarts.value.has(cartId) || chargingCarts.value.has(cartId)) return;
    const pluginId = lastLive.value?.pluginId;
    const mediaId = lastLive.value?.mediaId;

    // Demo path (no live target OR API not bound) — flip local state only.
    if (!pluginId || !mediaId || !pipelineAPI?.setCartStatus) {
      const next = new Set(sentCarts.value);
      next.add(cartId);
      sentCarts.value = next;
      if (resultData.value && next.size >= resultData.value.carts.length) {
        allChargesSent.value = true;
      }
      return;
    }

    const guardNext = new Set(chargingCarts.value);
    guardNext.add(cartId);
    chargingCarts.value = guardNext;
    try {
      await pipelineAPI.setCartStatus({
        pluginId,
        mediaId,
        buyerUserId: cartId,
        status: 'request_payment',
      });
      const next = new Set(sentCarts.value);
      next.add(cartId);
      sentCarts.value = next;
      if (resultData.value && next.size >= resultData.value.carts.length) {
        allChargesSent.value = true;
      }
    } finally {
      const releaseNext = new Set(chargingCarts.value);
      releaseNext.delete(cartId);
      chargingCarts.value = releaseNext;
    }
  };

  const chargeAll = async () => {
    if (!resultData.value) return;
    const pending = resultData.value.carts.filter((c) => !sentCarts.value.has(c.id));
    // Fire sequentially so we don't stampede the backend or the buyer
    // portal Redis lock (see cart-status/set concurrency guard on Go side).
    // eslint-disable-next-line no-restricted-syntax
    for (const c of pending) {
      // eslint-disable-next-line no-await-in-loop
      await chargeCart(c.id);
    }
  };

  const reset = () => {
    cancelProcessing();
    state.value = 'idle';
    processingStep.value = 0;
    processingProgress.value = 0;
    showConnectDrawer.value = false;
    resultData.value = null;
    sentCarts.value = new Set();
    allChargesSent.value = false;
  };

  // ── Waiting-live actions ─────────────────────────────────────────
  // Called by <PluginWaitingLive> on mount to point the store at the
  // specific plugin whose settings we're editing.
  const setWaitingPluginId = (id: string) => {
    if (waitingPluginId.value !== id) autoMessageEnabled.value = false;
    waitingPluginId.value = id;
  };

  // Hydrates from a GET /plugins/{id}/settings response so a refresh
  // resumes exactly where the seller left off.
  const hydrateWaitingFromSettings = (payload: {
    mode?: string;
    onboarding_step?: number;
    auto_message_enabled?: boolean;
  } | null) => {
    if (!payload) return;
    if (payload.mode === 'transcript' || payload.mode === 'catalog') {
      waitingMode.value = payload.mode;
    } else {
      waitingMode.value = null;
    }
    const step = typeof payload.onboarding_step === 'number' ? payload.onboarding_step : 0;
    waitingStep.value = (step === 1 || step === 2) ? step : 0;
    if (typeof payload.auto_message_enabled === 'boolean') {
      autoMessageEnabled.value = payload.auto_message_enabled;
    }
  };

  // Debounced persist — rapid state flips (e.g. clicking around) collapse
  // into a single PUT so we don't stampede the backend.
  const persistWaitingState = () => {
    if (!waitingPluginId.value || !pipelineAPI?.updateSettings) return;
    if (waitingPersistTimer) clearTimeout(waitingPersistTimer);
    waitingPersistTimer = setTimeout(() => {
      pipelineAPI!.updateSettings({
        pluginId: waitingPluginId.value,
        mode: waitingMode.value || '',
        onboardingStep: waitingStep.value,
      }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error('livvia.persistWaitingState failed', err);
      });
    }, 400);
  };

  const setWaitingStep = (n: WaitingStep) => {
    waitingStep.value = n;
    persistWaitingState();
  };

  const setWaitingMode = (m: WaitingMode) => {
    waitingMode.value = m;
    // Landing on a mode implies "advanced past choice" → step 2.
    if (m) waitingStep.value = 2;
    persistWaitingState();
  };

  // "Configurar depois" escape from the chooser or prep view.
  const resetWaitingFlow = () => {
    waitingMode.value = null;
    waitingStep.value = 0;
    persistWaitingState();
  };

  const openHowModal = () => { howModalOpen.value = true; };
  const closeHowModal = () => { howModalOpen.value = false; };

  // Confirm the toggle only after its welcome form and settings are saved.
  // Keep errors visible to the caller and serialize repeated clicks.
  const setWaitingAutoMessage = async (enabled: boolean) => {
    if (waitingAutoMessageSaving.value) return;
    const pluginId = waitingPluginId.value;
    const api = pipelineAPI;
    if (!pluginId || !api?.updateSettings || (enabled && !api.ensureDefaultForm)) {
      throw new Error('Welcome automation is not ready');
    }
    waitingAutoMessageSaving.value = true;
    try {
      if (enabled) await api.ensureDefaultForm!({ pluginId });
      await api.updateSettings({
        pluginId,
        autoMessageEnabled: enabled,
      });
      if (waitingPluginId.value === pluginId) autoMessageEnabled.value = enabled;
    } finally {
      waitingAutoMessageSaving.value = false;
    }
  };

  return {
    state,
    processingStep,
    processingProgress,
    showConnectDrawer,
    connectPlatform,
    autoMessageEnabled,
    waitingAutoMessageSaving,
    lastLive,
    resultData,
    sentCarts,
    chargingCarts,
    allChargesSent,
    waitingPluginId,
    waitingStep,
    waitingMode,
    howModalOpen,
    isVisible,
    setState,
    openConnect,
    deriveFromPlugins,
    activatePrepare,
    skipPrepare,
    enterPostLive,
    deferPostLive,
    startBuildingCarts,
    startProcessing,
    cancelProcessing,
    registerPipelineAPI,
    setPreparePluginId,
    chargeCart,
    chargeAll,
    reset,
    setWaitingPluginId,
    hydrateWaitingFromSettings,
    setWaitingStep,
    setWaitingMode,
    resetWaitingFlow,
    openHowModal,
    closeHowModal,
    setWaitingAutoMessage,
  };
});
